import qs from 'qs'
import { merge } from '@Utils/index'
import { BASE_URL } from '@/configs'
import axiosInstance from './axiosInstance'
import { BdMessage } from '@bilibili-business/bili-design'
import { isLan, isWan, isRegister, redirectSystemLogin, redirectWanLogin, redirectAccountLogin, redirectLanDashboard, withoutCookieAuth } from '@Utils/system'
import { verify } from '@/components/Verify/index.ts'
import { spmReport } from '@/utils/report'
import { nanoid } from 'nanoid'
/**
 * 初始化数据
 * @type {{baseUrl: string}}
 */
const reqContentTypes = {
  json: 'application/json', // json
  formData: 'multipart/form-data', // 消息
  xWwwFormUrlencoded: 'application/x-www-form-urlencoded' // 普通表单
}

const initCfg = {
  baseURL: BASE_URL
}
const ajax = {
  $instance: axiosInstance
}

const methods = {
  noDateRequest: ['delete', 'get', 'head', 'options'],
  hasDateRequest: ['post', 'put', 'patch']
}

/**
 * http请求的config 模式
 * @type {Promise<unknown>}
 */
ajax.request = getRequest()

/**
 * http 请求使用具体method 模式
 */
methods.noDateRequest.forEach(method => {
  ajax[method] = getNoDataRequest(method)
})

methods.hasDateRequest.forEach(method => {
  ajax[method] = getWithDataRequest(method)
})

/**
 * url不带参数的请求操作
 * @param method
 */
function getNoDataRequest(method) {
  return function (url, config = {}) {
    // 用于swagger替换接口字符串中的参数
    const reg = new RegExp(/\${(.*?)}/g)
    const _key = url.match(reg)?.[0] || ''
    const key = _key.substring(2, _key.length - 1)
    const newUrl = key ? url.replace(/\${(.*?)}/g, config.params[key]) : url

    if (key && config.params[key]) {
      delete config.params[key]
    }
    if (!config.params) {
      config.params = {}
    }
    //新增track_id 前后端日志串联
    config.params.ct_id = nanoid()

    return baseRequest(merge(config, { method, url: newUrl }))
  }
}

/**
 * url 带参数的请求操作
 * @param method
 */
function getWithDataRequest(method) {
  return function (url, config = {}) {
    // 用于swagger替换接口字符串中的参数
    const reg = new RegExp(/\${(.*?)}/g)
    const _key = url.match(reg)?.[0] || ''
    const key = _key.substring(2, _key.length - 1)
    const newUrl = key ? url.replace(/\${(.*?)}/g, config.data[key]) : url

    if (key && config.data[key]) {
      delete config.data[key]
    }

    const oCfs = {
      method: method,
      url: newUrl
    }
    const contentType = (config.headers && config.headers['Content-Type']) || 'application/json'
    // config.data = {account_id: store.getters.account_id, ...config.data}
    //新增track_id 前后端日志串联
    if (!config.params) {
      config.params = {}
    }
    config.params.ct_id = nanoid()

    if (config.data && contentType === reqContentTypes.xWwwFormUrlencoded) {
      oCfs.data = qs.stringify(config.data)
    }

    return baseRequest(merge(config, oCfs))
  }
}

// 请求的通用方法
function getRequest() {
  return function (config) {
    return baseRequest(config)
  }
}

// 请求的具体实现
function baseRequest(config) {
  return new Promise(function (resolve, reject) {
    // 兼容如何使用不同域名的头部
    axiosInstance.request(merge({}, { ...initCfg, ...config })).then(
      ({ status, data, error = {} }) => {
        if (status >= 200 && status < 300) {
          // withoutCookieAuth 目前兼容，理论上帮助中心应该拿出去单独做
          if (+data.code === 0 || data.status === 'success' || withoutCookieAuth()) {
            spmReport.reportApiNormal({
              api: config.url,
              method: config.method,
              code: data.code,
              status,
              msg: data.msg || data.error_msg
            })
            return resolve(data.result, data, status, config)
          }
          if (!config.slient) {
            BdMessage({
              type: 'error',
              message: data.msg || data.error_msg || '请求失败'
            })
          }

          // todo 加入业务逻辑， 建议以后改出来
          // todo lan相关，取消lan下跳转到404问题
          // 营销官网快捷登录判断
          if ([1005, 1006, 1007, 1008].includes(+data.code) && isWan()) {
            redirectSystemLogin()
            return
          } else if ([1015, 1016, 1017, 1019].includes(+data.code) && isWan()) {
            redirectAccountLogin()
            return
          } else if ([1004].includes(+data.code)) {
            if (!isRegister()) {
              redirectWanLogin()
              return
            }
          } else if (data.code === 10302 && isLan()) {
            // jump to dashboard
            redirectLanDashboard()
            return
          }
        }
        // 限流校验
        if (+data.code === 1031) {
          return verify(true, data.result)
            .then(() => {
              return resolve(baseRequest(config))
            })
            .catch(e => {
              return reject(new Error(e))
            })
        }

        spmReport.reportApiError({
          api: config.url,
          method: config.method,
          code: data.code,
          status,
          msg: data.msg || data.error_msg,
          ct_id: config.params?.ct_id
        })

        return reject({ status, data, config })
      },
      res => {
        let status = /(504.*)/.exec(res) ? 504 : 500
        spmReport.reportApiError({
          api: config.url,
          method: config.method,
          code: status,
          status: 'timeout',
          msg: '网络异常',
          ct_id: config.params?.ct_id
        })
        return reject({ res, status })
      }
    )
  })
}

export default ajax
